<template>
    <div class="fixed w-full h-full inset-0 bg-black/35 flex justify-center items-center z-50 px-5">
        <div
            class="relative lg:w-1/3 w-full h-fit flex flex-col justify-center p-5 items-center space-y-5 bg-white   rounded-xl">


            <div v-if="showLoadingOtpVerificationModal"
                class="absolute w-full h-full bg-black/20 flex justify-center items-center z-50 rounded-xl">
                <div class="flex justify-center space-x-2 mt-3">
                    <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-0"></span>
                    <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-150"></span>
                    <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-300"></span>
                </div>
            </div>



            <div v-if="successfulRequestOtp" class="w-full flex flex-col justify-center items-center">


                <div class="lg:w-full w-1/3 h-1/3 flex justify-center items-center">
                    <LockLogo />
                </div>

                <div class="h-2/3 flex flex-col items-center justify-start mt-2 space-y-2">
                    <!-- Title -->
                    <div class="text-lg font-medium">{{ $t('edit_otp.title') }}</div>

                    <!-- Description -->
                    <div class="text-gray-700 text-center w-4/5 lg:w-3/4">
                        {{ $t('edit_otp.description') }}
                    </div>

                    <!-- Code Inputs -->
                    <div class="flex justify-center items-center w-full space-x-5 pt-5 px-5 ">
                        <div class="w-5/6  mx-auto flex space-x-2">
                            <template v-for="(digit, index) in digits" :key="index">
                                <input type="text" maxlength="1" inputmode="numeric" pattern="[0-9]*"
                                    :ref="el => inputs[index] = el" v-model="digit.value"
                                    @input="(e) => handleInput(e, index)" @keydown="(e) => handleKeyDown(e, index)"
                                    :disabled="disabledInputs" :class="[
                                        'w-full rounded-lg outline-none text-center text-lg shadow px-3 py-4 transition duration-200 border',
                                        disabledInputs
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                            : 'hover:border-2 focus:border-primary',
                                        showError ? 'border-red-300' : 'border-gray-300'
                                    ]" />
                            </template>
                        </div>
                    </div>

                    <!-- Error Message -->
                    <div v-if="showError" class="text-red-400 text-sm lg:w-3/4 font-medium w-4/5 text-center">
                        {{ $t('edit_otp.error') }}
                    </div>

                    <!-- Resent Message -->
                    <div v-if="showResentMessage" class="text-primary text-sm font-medium text-center">
                        {{ $t('edit_otp.resent') }}
                    </div>

                    <!-- Resend Button -->
                    <div class="pt-2">
                        <button
                            class="text-primary font-medium transition duration-200 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
                            @click="startResendCooldown" :disabled="resendDisabled">
                            <span v-if="!resendDisabled">{{ $t('edit_otp.resend') }}</span>
                            <span v-else>{{ $t('edit_otp.resend_in', { seconds: resendCooldown }) }}</span>
                        </button>
                    </div>

                    <!-- Submit Button / Loading Animation -->
                    <div class="w-full flex flex-col lg:flex-row justify-center  space-y-2 lg:space-y-0 lg:space-x-2 items-center pt-5">
                        <div :class="[
                            showSubmitionAnimation ? 'hidden opacity-0' : 'block opacity-100',
                            'bg-primary w-1/2 lg:w-1/3 text-white text-center rounded py-2 px-3 hover:bg-secondary hover:cursor-pointer transition duration-200'
                        ]" @click="submitCode">
                            {{ $t('edit_otp.confirm') }}
                        </div>

                        <div :class="[
                            showSubmitionAnimation ? 'hidden opacity-0' : 'block opacity-100',
                            'bg-gray-300  w-1/2 lg:w-1/3 text-gray-700 text-center rounded py-2 px-3 hover:bg-secondary hover:cursor-pointer transition duration-200'
                        ]" @click="cancelOtpEdit">
                            {{ $t('edit_otp.cancel') }}
                        </div>
                        <div :class="[
                            !showSubmitionAnimation ? 'hidden' : 'flex',
                            'justify-center items-center space-x-2 mt-4'
                        ]">
                            <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-0"></span>
                            <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-150"></span>
                            <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-300"></span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="failedRequestOtp" class="text-center ">
                <div class="w-1/3 h-auto text-red-500 mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-full">
                        <path stroke-linecap="round" stroke-linejoin="round"
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                    </svg>
                </div>

                <div class="text-gray-800 font-medium"> Failed to send OTP for phone number </div>

            </div>

            <div v-if="successfulVerificationOtp" class="w-full flex flex-col justify-center items-center">
                <div class="w-1/3 h-auto text-primary mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-2/3 mx-auto">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                </div>

                <div class="text-primary font-medium"> Success Otp verification </div>


            </div>

        </div>
    </div>

</template>

<script setup>
import { requestOtpForPhoneNumberUpdate, verifyOtpForPhoneNumberUpdate } from '@/api/admin';
import { ref, defineProps, defineEmits, onMounted } from 'vue';
import LockLogo from '@/components/PhoneOtpLock.vue'

const digits = Array(5).fill('').map(() => ref(''))
const inputs = Array(5).fill(null).map(() => ref(null))


const emit = defineEmits(['error', 'success' , 'cancel']);


const props = defineProps({
    phone: {
        required: true,
        type: String,
    }
});

const showLoadingOtpVerificationModal = ref(false);

const successfulRequestOtp = ref(false);
const failedRequestOtp = ref(false);

onMounted(async () => {
    try {
        showLoadingOtpVerificationModal.value = true;
        let response = await requestOtpForPhoneNumberUpdate({ phone: props.phone });
        if (response.data.success) {
            successfulRequestOtp.value = true;
            failedRequestOtp.value = false;
        }
    } catch (error) {
        showLoadingOtpVerificationModal.value = false;
        successfulRequestOtp.value = false;
        failedRequestOtp.value = true;

        setTimeout(() => {

            let errors = error.response.data.errors;

            emit('error', errors);
            failedRequestOtp.value = false;
        }, 2000);

    }

    showLoadingOtpVerificationModal.value = false;


})



//otp 

const showSubmitionAnimation = ref(false)
const disabledInputs = ref(false)
const showError = ref(false)

const resendCooldown = ref(60)
const resendDisabled = ref(false)
const showResentMessage = ref(false)


const handleInput = (e, index) => {
    const value = e.target.value.replace(/\D/g, '')
    if (value) {
        digits[index].value = value[0]
        if (index < inputs.length - 1) {
            inputs[index + 1]?.focus()
        } else {

            const code = digits.map(d => d.value).join('')
            if (code.length === digits.length) {
                submitCode()
            }
        }
        showError.value = false
    } else {
        digits[index].value = ''
    }
}

const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !digits[index].value && index > 0) {
        inputs[index - 1]?.focus()
    }
}


const successfulVerificationOtp = ref(false);

const submitCode = async () => {

    showLoadingOtpVerificationModal.value = true;
    const code = digits.map(d => d.value).join('');

    try {
        const payload = {
            phone: props.phone,
            code
        };

        const response = await verifyOtpForPhoneNumberUpdate(payload);
        if (response.data.success) {

            showLoadingOtpVerificationModal.value = false;
            successfulRequestOtp.value = false;
            successfulVerificationOtp.value = true;
            setTimeout(() => {
                showLoadingOtpVerificationModal.value = false;
                successfulVerificationOtp.value = false;
                emit('success');

            }, 2000);


        } else {
            showLoadingOtpVerificationModal.value = false
            showError.value = true;

            // alert(response.data.message || t('edit_otp.error'));
        }


    } catch (error) {
        console.log('error :>> ', error);

        showLoadingOtpVerificationModal.value = false;
        showError.value = true;

    }


};



const toggleSubmitionError = (active) => {
    showError.value = active
}

const resetCode = () => {
    digits.forEach(d => d.value = '')
    showError.value = false
    inputs[0]?.focus()
}

const startResendCooldown = async () => {
    resendDisabled.value = true;
    resendCooldown.value = 60;
    showResentMessage.value = true;
    resetCode();

    try {
        const response = await requestOtpForPhoneNumberUpdate({ phone: props.phone });
        if (!response.data.success) {
            resendDisabled.value = false;
            showResentMessage.value = false;
            // alert(response.data.message || t('edit_otp.error'));
            return;
        }
    } catch (error) {
        console.error(error);
        resendDisabled.value = false;
        showResentMessage.value = false;
        // alert(t('edit_otp.error'));
        return;
    }

    const interval = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0) {
            clearInterval(interval);
            resendDisabled.value = false;
            showResentMessage.value = false;
        }
    }, 1000);
};


const cancelOtpEdit = () => { 
    emit("cancel");

}

</script>

<style scoped>
@keyframes bounce-wave {

    0%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-8px);
    }
}

.animate-bounce-wave {
    animation: bounce-wave 1.2s infinite ease-in-out;
}

.delay-0 {
    animation-delay: 0s;
}

.delay-150 {
    animation-delay: 0.15s;
}

.delay-300 {
    animation-delay: 0.3s;
}

/* Remove arrows for Chrome, Safari, Edge, and Opera */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Remove arrows for Firefox */
input[type="number"] {
    -moz-appearance: textfield;
}
</style>